import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Bell, Menu, X, LogOut, User, Shield, Monitor, Truck, Building2, Waypoints, HelpCircle, Sparkles, Map as MapIcon, ChevronDown, Camera, Tv, MonitorPlay } from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '@/lib/api';

export default function Header({ dark = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setOpen(false); setMobileOpen(false); setMoreOpen(false); }, [loc.pathname]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/notifications?limit=100');
        setUnread(data.filter((n) => !n.read).length);
      } catch {}
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, [user, loc.pathname]);

  const isAdmin = user?.role === 'admin';
  const isBusiness = isAdmin || user?.plan === 'business';
  const isPilot = user?.is_load_pilot && user?.pilot_status === 'approved';
  const canFreight = isBusiness || isPilot;
  const isOwner = user?.org_role === 'owner';

  return (
    <header className={`sticky top-0 z-40 border-b ${dark ? 'bg-black/80 border-white/10 backdrop-blur-md' : 'bg-white/85 border-zinc-200 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
          <img src="/brand/logo-icon@2x.png" alt="PTT NZ" className="w-9 h-9 rounded-lg" />
          <div className="block">
            <div className="font-display font-black text-base sm:text-lg leading-none">PTT NZ Alerts</div>
            <div className={`text-[9px] sm:text-[10px] tracking-[0.2em] uppercase ${dark ? 'text-teal-400' : 'text-zinc-500'}`}>NZ Highway Ops</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          <NavLink to="/alerts" active={loc.pathname.startsWith('/alerts')} dark={dark} testid="nav-alerts">Alerts</NavLink>
          <NavLink to="/map" active={loc.pathname === '/map'} dark={dark} testid="nav-map"><MapIcon className="w-3.5 h-3.5" /> Map</NavLink>
          {user && <NavLink to="/dashboard" active={loc.pathname === '/dashboard'} dark={dark} testid="nav-dashboard">Dashboard</NavLink>}
          {user && <NavLink to="/loads" active={loc.pathname.startsWith('/loads')} dark={dark} testid="nav-loads"><Waypoints className="w-3.5 h-3.5" /> Loads</NavLink>}
          {canFreight && <NavLink to="/restrictions" active={loc.pathname.startsWith('/restrictions')} dark={dark} testid="nav-restrictions"><Truck className="w-3.5 h-3.5" /> Freight</NavLink>}
          {isAdmin && <NavLink to="/admin" active={loc.pathname.startsWith('/admin')} dark={dark} testid="nav-admin"><Shield className="w-3.5 h-3.5" /> Admin</NavLink>}
          <div className="relative">
            <button onClick={() => setMoreOpen(!moreOpen)} data-testid="nav-more"
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition ${dark ? 'text-zinc-300 hover:bg-white/5' : 'text-zinc-700 hover:bg-zinc-100'}`}>
              More <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {moreOpen && (
              <div className={`absolute right-0 mt-2 w-52 rounded-xl border shadow-lg overflow-hidden z-50 ${dark ? 'bg-neutral-900 border-white/10' : 'bg-white border-zinc-200'}`} data-testid="nav-more-menu">
                <MoreItem to="/oversize" dark={dark} icon={<Truck className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-oversize">Oversize loads</MoreItem>
                {isOwner && <MoreItem to="/team" dark={dark} icon={<Building2 className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-team">Team</MoreItem>}
                <MoreItem to="/help" dark={dark} icon={<HelpCircle className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-help">Guide</MoreItem>
                <MoreItem to="/whats-new" dark={dark} icon={<Sparkles className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-whatsnew">What's New</MoreItem>
                {isBusiness && <MoreItem to="/noc" dark={dark} icon={<Monitor className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-noc">NOC overview</MoreItem>}
                {isBusiness && <MoreItem to="/noc/cameras" dark={dark} icon={<Camera className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-cctv">CCTV wall</MoreItem>}
                {isBusiness && <MoreItem to="/noc/vms" dark={dark} icon={<MonitorPlay className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-vms">VMS wall</MoreItem>}
                {isBusiness && <MoreItem to="/noc/screens" dark={dark} icon={<Tv className="w-4 h-4" />} onClick={() => setMoreOpen(false)} testid="more-noc-screens">NOC screens</MoreItem>}
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/notifications" className={`relative p-2 rounded-lg ${dark ? 'hover:bg-white/10' : 'hover:bg-zinc-100'}`} data-testid="header-notifications">
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </Link>
              <div className="relative">
                <button onClick={() => setOpen(!open)} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${dark ? 'hover:bg-white/10' : 'hover:bg-zinc-100'}`} data-testid="header-user-menu">
                  <div className="w-7 h-7 rounded-full bg-teal-500 text-black font-bold text-xs flex items-center justify-center">
                    {(user.full_name || user.email)[0].toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate whitespace-nowrap">{user.full_name || user.email.split('@')[0]}</span>
                </button>
                {open && (
                  <div className={`absolute right-0 mt-2 w-56 rounded-xl border shadow-lg overflow-hidden ${dark ? 'bg-neutral-900 border-white/10' : 'bg-white border-zinc-200'}`}>
                    <div className={`px-4 py-3 text-xs ${dark ? 'text-zinc-400 border-b border-white/10' : 'text-zinc-500 border-b border-zinc-200'}`}>
                      Signed in as<br /><span className={`font-semibold ${dark ? 'text-white' : 'text-zinc-900'}`}>{user.email}</span>
                    </div>
                    <Link to="/settings" onClick={() => setOpen(false)} className={`flex items-center gap-2 px-4 py-2.5 text-sm ${dark ? 'hover:bg-white/5' : 'hover:bg-zinc-50'}`} data-testid="menu-settings">
                      <User className="w-4 h-4" /> Profile & Settings
                    </Link>
                    <button onClick={() => { logout(); nav('/'); setOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm ${dark ? 'hover:bg-white/5 text-red-400' : 'hover:bg-zinc-50 text-red-600'}`} data-testid="menu-logout">
                      <LogOut className="w-4 h-4" /> Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost" data-testid="header-login">Log in</Link>
              <Link to="/signup" className="btn btn-primary" data-testid="header-signup">Sign up</Link>
            </>
          )}
          <button className={`lg:hidden p-2 rounded-lg ${dark ? 'hover:bg-white/10' : 'hover:bg-zinc-100'}`} onClick={() => setMobileOpen(!mobileOpen)} data-testid="header-mobile-toggle" aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className={`lg:hidden border-t ${dark ? 'bg-neutral-950 border-white/10' : 'bg-white border-zinc-200'}`} data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            <MobileLink to="/alerts" dark={dark} icon={<Bell className="w-4 h-4" />} testid="mnav-alerts">Alerts</MobileLink>
            <MobileLink to="/oversize" dark={dark} icon={<Truck className="w-4 h-4" />} testid="mnav-oversize">Oversize Loads</MobileLink>
            {user && <MobileLink to="/dashboard" dark={dark} icon={<Monitor className="w-4 h-4" />} testid="mnav-dashboard">Dashboard</MobileLink>}
            {user && <MobileLink to="/loads" dark={dark} icon={<Waypoints className="w-4 h-4" />} testid="mnav-loads">Loads</MobileLink>}
            {canFreight && <MobileLink to="/restrictions" dark={dark} icon={<Truck className="w-4 h-4" />} testid="mnav-restrictions">Freight</MobileLink>}
            {isOwner && <MobileLink to="/team" dark={dark} icon={<Building2 className="w-4 h-4" />} testid="mnav-team">Team</MobileLink>}
            <MobileLink to="/help" dark={dark} icon={<HelpCircle className="w-4 h-4" />} testid="mnav-help">Guide &amp; help</MobileLink>
            <MobileLink to="/map" dark={dark} icon={<MapIcon className="w-4 h-4" />} testid="mnav-map">Live map</MobileLink>
            <MobileLink to="/whats-new" dark={dark} icon={<Sparkles className="w-4 h-4" />} testid="mnav-whatsnew">What's New</MobileLink>
            {isAdmin && <MobileLink to="/admin" dark={dark} icon={<Shield className="w-4 h-4" />} testid="mnav-admin">Admin</MobileLink>}
            {isBusiness && <MobileLink to="/noc" dark={dark} icon={<Monitor className="w-4 h-4" />} testid="mnav-noc">NOC overview</MobileLink>}
            {isBusiness && <MobileLink to="/noc/cameras" dark={dark} icon={<Camera className="w-4 h-4" />} testid="mnav-cctv">CCTV wall</MobileLink>}
            {isBusiness && <MobileLink to="/noc/vms" dark={dark} icon={<MonitorPlay className="w-4 h-4" />} testid="mnav-vms">VMS wall</MobileLink>}
            {isBusiness && <MobileLink to="/noc/screens" dark={dark} icon={<Tv className="w-4 h-4" />} testid="mnav-noc-screens">NOC screens</MobileLink>}
            {user && <MobileLink to="/settings" dark={dark} icon={<User className="w-4 h-4" />} testid="mnav-settings">Profile &amp; Settings</MobileLink>}
            {user ? (
              <button onClick={() => { logout(); nav('/'); setMobileOpen(false); }} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${dark ? 'text-red-400 hover:bg-white/5' : 'text-red-600 hover:bg-zinc-50'}`} data-testid="mnav-logout">
                <LogOut className="w-4 h-4" /> Log out
              </button>
            ) : (
              <div className="flex gap-2 pt-2">
                <Link to="/login" className="btn btn-ghost flex-1" data-testid="mnav-login">Log in</Link>
                <Link to="/signup" className="btn btn-primary flex-1" data-testid="mnav-signup">Sign up</Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, dark, icon, testid, children }) {
  const loc = useLocation();
  const active = to === '/alerts' ? loc.pathname.startsWith('/alerts') : loc.pathname.startsWith(to);
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition
        ${active
          ? (dark ? 'bg-white/10 text-white' : 'bg-orange-50 text-orange-700')
          : (dark ? 'text-zinc-200 hover:bg-white/5' : 'text-zinc-700 hover:bg-zinc-100')}`}
    >
      {icon}{children}
    </Link>
  );
}

function MoreItem({ to, dark, icon, onClick, testid, children }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm ${dark ? 'hover:bg-white/5 text-zinc-200' : 'hover:bg-zinc-50 text-zinc-700'}`}>
      {icon}{children}
    </Link>
  );
}

function NavLink({ to, active, dark, testid, children }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition
        ${active
          ? (dark ? 'bg-white/10 text-white' : 'bg-orange-50 text-orange-700')
          : (dark ? 'text-zinc-300 hover:bg-white/5' : 'text-zinc-700 hover:bg-zinc-100')}`}
    >
      {children}
    </Link>
  );
}
